import { useRef, useState, type ReactNode } from "react";
import { X, Minimize2, GripHorizontal } from "lucide-react";
import { useTranslation } from "react-i18next";

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

interface FloatingWindowProps {
  /** Header content (left side) — e.g. a color dot + title + inline controls. */
  title: ReactNode;
  width?: number;
  bodyClassName?: string;
  /** Snap back to its docked placement. Omit to hide the dock button. */
  onDock?: () => void;
  onClose: () => void;
  children: ReactNode;
}

/**
 * A draggable, fixed-position floating window used to "pop out" any `/map`
 * section (video, timeline, category drawer …) and place it anywhere on screen.
 */
const FloatingWindow = ({
  title,
  width = 320,
  bodyClassName,
  onDock,
  onClose,
  children,
}: FloatingWindowProps) => {
  const { t } = useTranslation("map");
  const [pos, setPos] = useState(() => ({
    x: Math.max(16, window.innerWidth - width - 24),
    y: 110,
  }));
  const dragRef = useRef<{ dx: number; dy: number } | null>(null);

  const onMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    dragRef.current = { dx: e.clientX - pos.x, dy: e.clientY - pos.y };
    const move = (ev: MouseEvent) => {
      if (!dragRef.current) return;
      setPos({
        x: clamp(ev.clientX - dragRef.current.dx, 0, window.innerWidth - 120),
        y: clamp(ev.clientY - dragRef.current.dy, 0, window.innerHeight - 60),
      });
    };
    const up = () => {
      dragRef.current = null;
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
    };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  };

  return (
    <div
      className="fixed z-50 overflow-hidden rounded-xl border border-border bg-background/95 shadow-[0_8px_32px_rgba(0,0,0,0.25)] backdrop-blur"
      style={{ left: pos.x, top: pos.y, width }}
    >
      <header
        onMouseDown={onMouseDown}
        className="flex cursor-move select-none items-center justify-between gap-2 border-b border-border px-3 py-2"
      >
        <div className="flex min-w-0 items-center gap-2 text-sm">
          <GripHorizontal className="h-4 w-4 shrink-0 text-muted-foreground" />
          {title}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {onDock && (
            <button
              type="button"
              onClick={onDock}
              aria-label={t("floatingWindow.dock")}
              title={t("floatingWindow.dockTitle")}
              className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <Minimize2 className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label={t("floatingWindow.close")}
            className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </header>
      <div className={bodyClassName}>{children}</div>
    </div>
  );
};

export default FloatingWindow;
